<template>
  <div class="individual-post-container flex flex-col" :data-post-id="post.post_id">    
    <!-- 主要數據容器 -->
    <div class="main-data-container bg-gray-600 rounded-lg my-1">
      <!-- 主要數據行 -->
      <div class="individual-post flex items-center py-2 mt-2 sm:mt-0 text-14px border-grey-3 sm:w-auto sm:w-full sm:grid sm:grid-cols-[7fr_4fr_4fr_4fr_4fr_4fr_4fr]">
        <!-- 模型標題 -->
        <div class='w-40 flex-shrink-0 sm:w-auto px-2 post-name break-word'>
          <a :href="getPostLink(post)" class="cursor-pointer">
            {{ post.post_title }}
          </a>
        </div>
        
        <!-- 收入 -->
        <div class='w-24 flex-shrink-0 ml-2 sm:ml-0 sm:w-auto px-1 post-revenue'>
          {{ formatValue(post.total_revenue, 'currency') }}
        </div>
        
        <!-- 讚數 -->
        <div class='w-20 flex-shrink-0 ml-2 sm:ml-0 sm:w-auto px-1 post-like'>
          {{ formatValue(post.total_likes) }}
        </div>
        
        <!-- 觀看數 -->
        <div class='w-20 flex-shrink-0 ml-2 sm:ml-0 sm:w-auto px-1 post-view'>
          {{ formatValue(post.total_views) }}
        </div>
        
        <!-- 下載數 -->
        <div class='w-15 flex-shrink-0 ml-2 sm:ml-0 sm:w-auto px-1 post-download'>
          {{ formatValue(post.total_downloads) }}
        </div>
        
        <!-- 收藏數 -->
        <div class='w-12 flex-shrink-0 ml-2 sm:ml-0 sm:w-auto px-1 post-save'>
          {{ formatValue(post.save_count) }}
        </div>
        
        <!-- 評分和展開按鈕 -->
        <div class='w-18 flex-shrink-0 ml-2 sm:ml-0 sm:w-auto px-1 post-rating flex items-center justify-between'>
          <span>{{ formatValue(post.post_rating, 'rating') }} / 5</span>
          
          <!-- 展開收起按鈕 -->
          <button 
            class="individual-post-collapse-btn inline-block flex justify-center items-center mr-2 lg:mr-6"
            :disabled="loadingRevenue"
            @click="handleToggleRevenue"
          >
            <div class="relative">
              <!-- Loading 狀態 -->
              <div 
                v-if="loadingRevenue"
                class="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-500"
              ></div>
              
              <!-- 展開收起圖標 -->
              <svg 
                v-else
                class="w-4 h-4 transition-transform duration-200"
                :class="{ 'rotate-180': isExpanded }"
                fill="none" 
                stroke="currentColor" 
                viewBox="0 0 24 24"
              >
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
              </svg>
            </div>
          </button>
        </div>
      </div>

      <!-- 收入詳情（展開時顯示） -->
      <transition name="expand">
        <div v-if="isExpanded && revenueDetails.length" class="revenue-details">
          <hr class="my-2 border-gray-500 mx-2">
          <div 
            v-for="variant in revenueDetails"
            :key="variant.post_variant_id"
            class="individual-post-revenues flex items-center mx-2 my-2 py-2 text-14px rounded-lg bg-gray-500 sm:grid sm:grid-cols-[7fr_4fr_4fr_4fr_4fr_4fr_4fr]"
          >
            <!-- 變體名稱 -->
            <div class='w-40 flex-shrink-0 sm:w-auto px-2 post-name break-word flex items-center'>
              <span>{{ variant.name }}</span>
              
              <!-- Phrozen 驗證標誌 -->
              <div v-if="variant.phrozen_verified" class="ml-2" title="Phrozen Verified">
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <rect width="20" height="20" rx="5" fill="#E60044"/>
                  <path d="M9.99989 12.5769C12.2303 12.5769 14.0384 10.7689 14.0384 8.53847C14.0384 6.30808 12.2303 4.5 9.99989 4.5C7.76951 4.5 5.96143 6.30808 5.96143 8.53847C5.96143 10.7689 7.76951 12.5769 9.99989 12.5769Z" stroke="white" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                  <path d="M10.0002 10.1536C10.8923 10.1536 11.6155 9.43039 11.6155 8.53824C11.6155 7.64609 10.8923 6.92285 10.0002 6.92285C9.108 6.92285 8.38477 7.64609 8.38477 8.53824C8.38477 9.43039 9.108 10.1536 10.0002 10.1536Z" fill="white"/>
                  <path d="M12.1539 15L9.99973 12.8462L7.84619 15" stroke="white" stroke-width="1.5" stroke-linecap="square" stroke-linejoin="round"/>
                </svg>
              </div>
            </div>
            
            <!-- 變體收入 -->
            <div class='w-24 flex-shrink-0 ml-2 sm:ml-0 sm:w-auto px-1 post-revenue'>
              ${{ variant.total_price.toFixed(2) }}
            </div>
            
            <!-- 其他欄位顯示 "-" -->
            <div class='w-20 flex-shrink-0 ml-2 sm:ml-0 sm:w-auto px-1 post-like text-gray-500'>-</div>
            <div class='w-20 flex-shrink-0 ml-2 sm:ml-0 sm:w-auto px-1 post-view text-gray-500'>-</div>
            <!-- 變體下載數 -->
            <div class='w-15 flex-shrink-0 ml-2 sm:ml-0 sm:w-auto px-1 post-download text-gray-500'>
              -
            </div>
            <div class='w-12 flex-shrink-0 ml-2 sm:ml-0 sm:w-auto px-1 post-save text-gray-500'>-</div>
            <div class='w-18 flex-shrink-0 ml-2 sm:ml-0 sm:w-auto px-1 post-rating text-gray-500'>-</div>
          </div>
        </div>
      </transition>

      <!-- 無收入詳情時的提示 -->
      <div 
        v-if="isExpanded && !revenueDetails.length && !loadingRevenue" 
        class="mt-2 ml-4 p-4 bg-gray-700 rounded-lg text-gray-400 text-center"
      >
        No revenue details available for this period.
      </div>
    </div>
  </div>
</template>

<script>
import { inject } from 'vue'

export default {
  name: 'AnalyticsItem',
  props: {
    post: {
      type: Object,
      required: true
    },
    index: {
      type: Number,
      required: true
    },
    isExpanded: {
      type: Boolean,
      default: false
    },
    revenueDetails: {
      type: Array,
      default: () => []
    },
    loadingRevenue: {
      type: Boolean,
      default: false
    }
  },
  emits: ['toggle-revenue'],
  setup(props, { emit }) {
    // 從父組件注入 analytics 實例
    const analytics = inject('analytics')
    
    const {
      formatValue,
      getPostLink
    } = analytics || {}

    // 處理展開收起
    const handleToggleRevenue = () => {
      emit('toggle-revenue', props.post.post_id)
    }

    return {
      formatValue,
      getPostLink,
      handleToggleRevenue
    }
  }
}
</script> 